import { useEffect, useRef, useState } from "react";
import { SubscriptionDetail, type SubscriptionDetailNotice } from "./SubscriptionDetail";
import { useSubscriptionBilling } from "../../../hooks/useSubscriptionBilling";
import { useAdminTiers } from "../../../hooks/useAdminTiers";
import type { MutationResult, SubscriptionSummary } from "../../../types/billing";
import styles from "./SubscriptionDetailModal.module.css";

// Fleet-page detail modal: loads a subscription by id (+ its entitlement when
// linked) and renders the same SubscriptionDetail body used inline on the
// instance panel, with full mutation wiring. Closing refreshes the fleet list.
type SubscriptionDetailModalProps = {
  readonly summary: SubscriptionSummary;
  readonly onClose: () => void;
};

export const SubscriptionDetailModal = ({ summary, onClose }: SubscriptionDetailModalProps): JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const billing = useSubscriptionBilling(summary.stripeSubscriptionId, summary.tenantId);
  const { tiers } = useAdminTiers();
  const activeTiers = tiers.filter((t) => t.active).map((t) => ({ tierCode: t.tierCode, displayName: t.displayName }));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SubscriptionDetailNotice>(null);
  const [linkInput, setLinkInput] = useState("");

  useEffect(() => {
    if (!dialogRef.current?.open) dialogRef.current?.showModal();
  }, []);

  const run = async (action: () => Promise<MutationResult>, okText: string): Promise<void> => {
    setBusy(true);
    setNotice(null);
    const result = await action();
    setNotice(result.ok ? { tone: "ok", text: okText } : { tone: "error", text: result.message });
    setBusy(false);
  };

  const handlePortal = async (): Promise<void> => {
    setBusy(true);
    setNotice(null);
    const result = await billing.openPortal();
    if (result.ok) window.open(result.url, "_blank", "noopener,noreferrer");
    else setNotice({ tone: "error", text: result.message });
    setBusy(false);
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h2 className={styles.title}>{summary.customerEmail ?? summary.stripeCustomerId}</h2>
          <p className={styles.subtitle}>
            {summary.stripeSubscriptionId}
            {summary.tenantId !== null ? ` · ${summary.tenantId}` : " · unlinked"}
          </p>
        </header>

        {billing.status === "loading" && <p className={styles.note}>Loading…</p>}
        {billing.status === "error" && (
          <p className={styles.noteError}>Couldn&apos;t load{billing.error !== null ? `: ${billing.error}` : "."}</p>
        )}
        {billing.status === "ready" && billing.subscription !== null && (
          <>
            {billing.subscription.tenantId === null && (
              <form
                className={styles.linkForm}
                onSubmit={(e) => {
                  e.preventDefault();
                  const tid = linkInput.trim();
                  if (tid.length === 0) return;
                  void run(() => billing.linkTenant(tid), "Linked to tenant.");
                }}
              >
                <label className={styles.linkLabel} htmlFor="link-tenant-id">
                  Link to tenant
                </label>
                <div className={styles.linkRow}>
                  <input
                    id="link-tenant-id"
                    className={styles.linkInput}
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    placeholder="tenant id (e.g. dev-001-nhnd95)"
                    disabled={busy}
                  />
                  <button
                    type="submit"
                    className={styles.linkBtn}
                    disabled={busy || linkInput.trim().length === 0}
                  >
                    Link
                  </button>
                </div>
                <p className={styles.linkHint}>
                  Assign this subscription to a tenant when the provisioning auto-link was ambiguous.
                </p>
              </form>
            )}
            <SubscriptionDetail
              subscription={billing.subscription}
              entitlement={billing.entitlement}
              tiers={activeTiers}
              busy={busy}
              notice={notice}
              onChangeQuantity={(si, qty) => void run(() => billing.changeItemQuantity(si, qty), "Quantity updated.")}
              onRemoveItem={(si) => void run(() => billing.removeItem(si), "Item removed.")}
              onSetTier={(tier, interval) => void run(() => billing.setTier(tier, interval), "Plan updated.")}
              onSync={() => void run(() => billing.sync(), "Synced from Stripe.")}
              onOpenPortal={() => void handlePortal()}
              onClose={onClose}
            />
          </>
        )}

        {/* When the detail is shown, Close is rendered inline with its actions
            (above); only fall back to a standalone footer Close otherwise. */}
        {!(billing.status === "ready" && billing.subscription !== null) && (
          <div className={styles.footer}>
            <button type="button" className={styles.closeBtn} onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
};
